import React, { useState, useEffect } from "react";
import "./Packages.css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import AxiosUrl from "../AxiosUrl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/** ISO date → value for <input type="datetime-local"> in the browser's time zone. */
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Dashboard offer banner (backend utils/Payment/homeOffer.js): what it may show, in its order
const HOME_OFFERS = [
  ["walletOffer", "Personal 2× top-up offer (from an automation), with its countdown"],
  ["packageEnding", "Package ending (3 days or ≤ 2 contacts left)"],
  ["packageEnded", "Package ended or used up in the last 14 days (shows the best-value package)"],
  ["yearlyOffer", "Yearly offer for members without a package (sale price and countdown during a sale)"],
];
const ALL_HOME_OFFERS = HOME_OFFERS.map(([key]) => key);

function Packages() {
  const uid = localStorage.getItem("id");
  const token = localStorage.getItem("token");

  const [packages, setPackages] = useState([]);
  const [paymentConfig, setPaymentConfig] = useState({
    oneTimePrice: 200,
    durations: [],
    oneTimePaymentEnabled: true
  });
  const [loading, setLoading] = useState(false);

  // Package Dialog
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    packageId: "",
    price: "",
    contacts: "",
    isActive: true
  });

  // Config Dialog
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configForm, setConfigForm] = useState({
    oneTimePrice: 200,
    durations: [],
    affiliateCommissionPercent: 20,
    friendCommissionPercent: 20,
    oneTimePaymentEnabled: true
  });

  // Yearly offer: regular price + daily contacts (backend config/offerConfig.js)
  const [offerInfo, setOfferInfo] = useState({ regularPrice: 3000, dailyContacts: 5 });
  // 1-hour offer on the first call/chat tap: members who saw it / still running / bought
  const [flashStats, setFlashStats] = useState(null);
  // Free interests today: { usedToday, atLimitToday }
  const [interestStats, setInterestStats] = useState(null);

  // View mode: 'grid' or 'list' - default to list
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    fetchPackages();
    fetchPaymentConfig();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await AxiosUrl.get(`/admin/packages/${uid}`, {
        headers: { token }
      });

      if (response.data.Error) {
        toast.error(response.data.Error);
      } else {
        setPackages(response.data.packages || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch packages");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentConfig = async () => {
    try {
      const response = await AxiosUrl.get(`/admin/payment-config/${uid}`, {
        headers: { token }
      });

      if (response.data.Error) {
        toast.error(response.data.Error);
      } else {
        const defaultConfig = {
          oneTimePrice: 200,
          durations: [],
          affiliateCommissionPercent: 20,
          friendCommissionPercent: 20,
          oneTimePaymentEnabled: true
        };
        const config = response.data.config || defaultConfig;
        // Ensure oneTimePaymentEnabled defaults to true if undefined (backward compatibility)
        if (config.oneTimePaymentEnabled === undefined) {
          config.oneTimePaymentEnabled = true;
        }
        setPaymentConfig(config);
        setConfigForm(config);
        if (response.data.offer) setOfferInfo(response.data.offer);
        if (response.data.flashOfferStats) setFlashStats(response.data.flashOfferStats);
        if (response.data.freeInterestStats) setInterestStats(response.data.freeInterestStats);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch payment config");
    }
  };

  const handleOpenPackageDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setPackageForm({
        packageId: pkg.packageId,
        price: pkg.price,
        contacts: pkg.contacts,
        isActive: pkg.isActive
      });
    } else {
      setEditingPackage(null);
      setPackageForm({
        packageId: "",
        price: "",
        contacts: "",
        isActive: true
      });
    }
    setPackageDialogOpen(true);
  };

  const handleClosePackageDialog = () => {
    setPackageDialogOpen(false);
    setEditingPackage(null);
  };

  const handleSavePackage = async () => {
    if (!packageForm.packageId || !packageForm.price || !packageForm.contacts) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      let response;
      if (editingPackage) {
        // Update
        response = await AxiosUrl.put(`/admin/packages/${uid}`, packageForm, {
          headers: { token }
        });
      } else {
        // Create
        response = await AxiosUrl.post(`/admin/packages/${uid}`, packageForm, {
          headers: { token }
        });
      }

      if (response.data.Error) {
        toast.error(response.data.Error);
      } else {
        toast.success(response.data.Message);
        handleClosePackageDialog();
        fetchPackages();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to save package");
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm("Are you sure you want to delete this package?")) {
      return;
    }

    try {
      const response = await AxiosUrl.delete(`/admin/packages/${uid}`, {
        headers: { token },
        data: { packageId }
      });

      if (response.data.Error) {
        toast.error(response.data.Error);
      } else {
        toast.success(response.data.Message);
        fetchPackages();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete package");
    }
  };

  const handleOpenConfigDialog = () => {
    // Start from the saved settings: edits left in a cancelled dialog (a sale price,
    // a 1-hour offer price) must never be saved later with an unrelated change
    setConfigForm(paymentConfig);
    setConfigDialogOpen(true);
  };

  const handleCloseConfigDialog = () => {
    setConfigDialogOpen(false);
  };

  const handleSaveConfig = async () => {
    // Yearly offer sale: an empty price clears it (with its times)
    const salePrice = configForm.offerSalePrice === "" || configForm.offerSalePrice == null ? null : Number(configForm.offerSalePrice);
    if (salePrice !== null) {
      if (!(salePrice > 0) || salePrice >= offerInfo.regularPrice) {
        toast.error(`Offer sale price must be less than ৳${offerInfo.regularPrice}`);
        return;
      }
      if (!configForm.offerSaleEndsAt) {
        toast.error("Set when the offer sale ends");
        return;
      }
    }
    // 1-hour offer: a lower price for one package (defaults: on, ৳2000 package, ৳1000, 60 min)
    const flashPackageId = configForm.flashOfferPackageId || "2000";
    const flashPrice = Number(configForm.flashOfferPrice ?? 1000);
    const flashPkg = packages.find((p) => String(p.packageId) === String(flashPackageId));
    // Checked when the offer's package/price change or it is switched on, once the
    // package list has loaded (the server checks the same); never for unrelated saves
    const flashChanged =
      String(flashPackageId) !== String(paymentConfig.flashOfferPackageId || "2000") ||
      flashPrice !== Number(paymentConfig.flashOfferPrice ?? 1000) ||
      paymentConfig.flashOfferEnabled === false;
    if (configForm.flashOfferEnabled !== false && flashChanged && packages.length > 0) {
      if (!flashPkg || !flashPkg.isActive) {
        toast.error("The call/chat offer package must be an active package");
        return;
      }
      if (!(flashPrice > 0) || flashPrice >= flashPkg.price) {
        toast.error(`Call/chat offer price must be less than ৳${flashPkg.price}`);
        return;
      }
    }
    const body = {
      ...configForm,
      flashOfferEnabled: configForm.flashOfferEnabled !== false,
      flashOfferPackageId: flashPackageId,
      flashOfferPrice: flashPrice,
      // Only while the offer is on: a half-typed value in the greyed-out field must not block saving
      ...(configForm.flashOfferEnabled !== false ? { flashOfferMinutes: Number(configForm.flashOfferMinutes ?? 60) } : {}),
      // Free members' interests and shortlist alerts; the number only while the limit is on
      freeInterestLimitEnabled: configForm.freeInterestLimitEnabled === true,
      freeInterestLimitWomen: configForm.freeInterestLimitWomen === true,
      shortlistAlertNeedsPackage: configForm.shortlistAlertNeedsPackage === true,
      shortlistViewNeedsPackage: configForm.shortlistViewNeedsPackage === true,
      // Dashboard offer banner; kinds only once set (missing = all)
      homeOfferEnabled: configForm.homeOfferEnabled === true,
      ...(Array.isArray(configForm.homeOfferKinds) ? { homeOfferKinds: configForm.homeOfferKinds } : {}),
      shortlistViewLockWomen: configForm.shortlistViewLockWomen === true,
      ...(configForm.freeInterestLimitEnabled === true ? { freeInterestDailyLimit: Number(configForm.freeInterestDailyLimit ?? 3) } : { freeInterestDailyLimit: undefined }),
      offerSalePrice: salePrice,
      offerSaleStartsAt: salePrice === null ? null : configForm.offerSaleStartsAt || null,
      offerSaleEndsAt: salePrice === null ? null : configForm.offerSaleEndsAt || null,
    };
    try {
      const response = await AxiosUrl.put(`/admin/payment-config/${uid}`, body, {
        headers: { token }
      });

      if (response.data.Error) {
        toast.error(response.data.Error);
      } else {
        toast.success(response.data.Message);
        handleCloseConfigDialog();
        fetchPaymentConfig();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to save config");
    }
  };

  const handleDurationChange = (index, field, value) => {
    const newDurations = [...configForm.durations];
    newDurations[index] = { ...newDurations[index], [field]: value };
    setConfigForm({ ...configForm, durations: newDurations });
  };

  return (
    <div className="packages-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="packages-header">
        <h1>Package Management</h1>
        <div className="header-actions">
          <Button
            variant="contained"
            color="secondary"
            onClick={handleOpenConfigDialog}
            style={{ marginRight: "10px" }}
          >
            Payment Settings
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenPackageDialog()}
          >
            Add Package
          </Button>
        </div>
      </div>

      {/* View Toggle Buttons */}
      <div className="packages-view-toggle">
        <IconButton
          onClick={() => setViewMode('grid')}
          color={viewMode === 'grid' ? 'primary' : 'default'}
          title="Grid View"
          style={{
            backgroundColor: viewMode === 'grid' ? '#1976d2' : '#f5f5f5',
            color: viewMode === 'grid' ? 'white' : '#666',
            marginRight: '8px'
          }}
        >
          <ViewModuleIcon />
        </IconButton>
        <IconButton
          onClick={() => setViewMode('list')}
          color={viewMode === 'list' ? 'primary' : 'default'}
          title="List View"
          style={{
            backgroundColor: viewMode === 'list' ? '#1976d2' : '#f5f5f5',
            color: viewMode === 'list' ? 'white' : '#666'
          }}
        >
          <ViewListIcon />
        </IconButton>
      </div>

      {loading ? (
        <div className="loading">Loading packages...</div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="packages-grid-container">
              {packages.length === 0 ? (
                <div className="packages-no-data">
                  <LocalOfferIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
                  <Typography variant="h6" style={{ color: '#666', marginBottom: 8 }}>
                    No packages found
                  </Typography>
                  <Typography variant="body2" style={{ color: '#999' }}>
                    Create your first package!
                  </Typography>
                </div>
              ) : (
                <div className="packages-grid">
                  {packages.map((pkg) => (
                    <Card key={pkg.packageId} className="packages-grid-card">
                      <CardContent className="packages-grid-card-content">
                        <div className="packages-grid-header">
                          <div className="packages-grid-icon-wrapper">
                            <LocalOfferIcon className="packages-grid-icon" />
                          </div>
                          <div className="packages-grid-header-info">
                            <Typography variant="h5" className="packages-grid-package-id">
                              Package #{pkg.packageId}
                            </Typography>
                            <Chip
                              label={pkg.isActive ? "Active" : "Inactive"}
                              size="small"
                              className={pkg.isActive ? "packages-status-active" : "packages-status-inactive"}
                              style={{
                                backgroundColor: pkg.isActive ? "#4caf50" : "#f44336",
                                color: "white",
                                fontSize: "11px",
                                height: "24px",
                                fontWeight: "600",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                              }}
                            />
                          </div>
                        </div>
                        
                        <div className="packages-grid-details">
                          <div className="packages-grid-detail-item">
                            <div className="packages-grid-detail-icon">
                              <AttachMoneyIcon />
                            </div>
                            <div className="packages-grid-detail-content">
                              <Typography variant="caption" className="packages-grid-label">Price</Typography>
                              <Typography variant="h6" className="packages-grid-value">
                                ৳{pkg.price}
                              </Typography>
                            </div>
                          </div>
                          
                          <div className="packages-grid-detail-item">
                            <div className="packages-grid-detail-icon">
                              <PeopleIcon />
                            </div>
                            <div className="packages-grid-detail-content">
                              <Typography variant="caption" className="packages-grid-label">Contacts</Typography>
                              <Typography variant="h6" className="packages-grid-value">
                                {pkg.contacts}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                      <CardActions className="packages-grid-actions">
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenPackageDialog(pkg)}
                          className="packages-grid-edit-btn"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeletePackage(pkg.packageId)}
                          className="packages-grid-delete-btn"
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* List/Table View */}
          {viewMode === 'list' && (
            <TableContainer component={Paper} className="table-container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Package ID</strong></TableCell>
                    <TableCell align="right"><strong>Price (BDT)</strong></TableCell>
                    <TableCell align="right"><strong>Contacts</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {packages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No packages found. Create your first package!
                      </TableCell>
                    </TableRow>
                  ) : (
                    packages.map((pkg) => (
                      <TableRow key={pkg.packageId}>
                        <TableCell>{pkg.packageId}</TableCell>
                        <TableCell align="right">৳{pkg.price}</TableCell>
                        <TableCell align="right">{pkg.contacts}</TableCell>
                        <TableCell align="center">
                          <span className={pkg.isActive ? "status-active" : "status-inactive"}>
                            {pkg.isActive ? "Active" : "Inactive"}
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenPackageDialog(pkg)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeletePackage(pkg.packageId)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Package Dialog */}
      <Dialog open={packageDialogOpen} onClose={handleClosePackageDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPackage ? "Edit Package" : "Create Package"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Package ID"
            fullWidth
            margin="normal"
            value={packageForm.packageId}
            onChange={(e) => setPackageForm({ ...packageForm, packageId: e.target.value })}
            disabled={!!editingPackage}
            helperText={editingPackage ? "Cannot change package ID" : "Unique identifier (e.g., '1000')"}
          />
          <TextField
            label="Price (BDT)"
            fullWidth
            margin="normal"
            type="number"
            value={packageForm.price}
            onChange={(e) => setPackageForm({ ...packageForm, price: parseInt(e.target.value) || "" })}
          />
          <TextField
            label="Number of Contacts"
            fullWidth
            margin="normal"
            type="number"
            value={packageForm.contacts}
            onChange={(e) => setPackageForm({ ...packageForm, contacts: parseInt(e.target.value) || "" })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={packageForm.isActive}
                onChange={(e) => setPackageForm({ ...packageForm, isActive: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePackageDialog}>Cancel</Button>
          <Button onClick={handleSavePackage} variant="contained" color="primary">
            {editingPackage ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Config Dialog */}
      <Dialog open={configDialogOpen} onClose={handleCloseConfigDialog} maxWidth="md" fullWidth>
        <DialogTitle>Payment Configuration</DialogTitle>
        <DialogContent>
          <TextField
            label="One-Time Contact Price (BDT)"
            fullWidth
            margin="normal"
            type="number"
            value={configForm.oneTimePrice}
            onChange={(e) => setConfigForm({ ...configForm, oneTimePrice: parseInt(e.target.value) || 200 })}
            disabled={!configForm.oneTimePaymentEnabled}
            helperText={configForm.oneTimePaymentEnabled ? "Price for one-time contact unlock" : "Enable one-time payment to set price"}
          />

          <FormControlLabel
            control={
              <Switch
                checked={configForm.oneTimePaymentEnabled !== false}
                onChange={(e) => setConfigForm({ ...configForm, oneTimePaymentEnabled: e.target.checked })}
                color="primary"
              />
            }
            label="Enable One-Time Payment"
            style={{ marginTop: "10px", marginBottom: "10px" }}
          />
          <p style={{ marginTop: "0", marginBottom: "20px", color: "#666", fontSize: "14px" }}>
            When enabled, users can see and purchase one-time contact unlock option in mobile and web apps.
            When disabled, only subscription packages will be shown.
          </p>

          <h3 style={{ marginTop: "20px" }}>Commission Settings</h3>
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
            <TextField
              label="Affiliate Commission (%)"
              type="number"
              value={configForm.affiliateCommissionPercent || 20}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                setConfigForm({
                  ...configForm,
                  affiliateCommissionPercent: Math.min(Math.max(value, 0), 100)
                });
              }}
              style={{ flex: 1 }}
              inputProps={{ min: 0, max: 100 }}
              helperText="Commission for affiliate referrals (0-100%)"
            />
            <TextField
              label="Friend Commission (%)"
              type="number"
              value={configForm.friendCommissionPercent || 20}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                setConfigForm({
                  ...configForm,
                  friendCommissionPercent: Math.min(Math.max(value, 0), 100)
                });
              }}
              style={{ flex: 1 }}
              inputProps={{ min: 0, max: 100 }}
              helperText="Commission for friend referrals (0-100%)"
            />
          </div>

          <h3 style={{ marginTop: "20px" }}>bKash payment check</h3>
          <p style={{ marginTop: "0", marginBottom: "10px", color: "#666", fontSize: "14px" }}>
            Every 10 minutes the server asks bKash about payments the app never finished. If bKash took the money, the
            purchase or top-up is credited to the member (who gets an SMS). Everything found is listed on the Payment
            Errors page (reconcile_…).
          </p>
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
            <TextField
              select
              label="When bKash took money but nothing was credited"
              value={configForm.reconcileMode || "credit"}
              onChange={(e) => setConfigForm({ ...configForm, reconcileMode: e.target.value })}
              style={{ flex: "1 1 280px" }}
            >
              <MenuItem value="credit">Credit it automatically</MenuItem>
              <MenuItem value="report">Only list it (staff credit by hand)</MenuItem>
              <MenuItem value="off">Don't check</MenuItem>
            </TextField>
            <TextField
              label="Credit automatically from"
              type="datetime-local"
              value={toLocalInput(configForm.reconcileCreditFrom)}
              onChange={(e) => setConfigForm({ ...configForm, reconcileCreditFrom: e.target.value ? new Date(e.target.value).toISOString() : null })}
              disabled={(configForm.reconcileMode || "credit") !== "credit"}
              InputLabelProps={{ shrink: true }}
              style={{ flex: "1 1 220px" }}
              helperText="Before this time payments are only listed. Empty = straight away."
            />
          </div>

          <h3 style={{ marginTop: "20px" }}>Yearly offer sale</h3>
          <p style={{ marginTop: "0", marginBottom: "10px", color: "#666", fontSize: "14px" }}>
            The yearly offer ({offerInfo.dailyContacts} contacts every day) costs ৳{offerInfo.regularPrice}. Set a lower price
            with an end time to run a limited-time sale: the app and web charge the sale price until it ends, and the
            app's offer page (bibahobangla://offer) shows ৳{offerInfo.regularPrice} crossed out with a countdown.
            Leave the price empty for no sale.
          </p>
          {(() => {
            const price = Number(configForm.offerSalePrice);
            const start = configForm.offerSaleStartsAt ? new Date(configForm.offerSaleStartsAt) : null;
            const end = configForm.offerSaleEndsAt ? new Date(configForm.offerSaleEndsAt) : null;
            const now = new Date();
            let status = "No sale";
            let color = "#666";
            if (price > 0 && end) {
              if (end <= now) { status = "Sale has ended"; color = "#9e9e9e"; }
              else if (start && start > now) { status = `Starts ${start.toLocaleString("en-GB")}`; color = "#1976d2"; }
              else { status = `Running: ৳${price} until ${end.toLocaleString("en-GB")}`; color = "#2e7d32"; }
            }
            return <p style={{ margin: "0 0 10px", fontWeight: 600, color }}>{status}</p>;
          })()}
          <div style={{ display: "flex", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
            <TextField
              label="Sale price (BDT)"
              type="number"
              value={configForm.offerSalePrice ?? ""}
              onChange={(e) => setConfigForm({ ...configForm, offerSalePrice: e.target.value })}
              inputProps={{ min: 1, max: offerInfo.regularPrice - 1 }}
              helperText={`Less than ৳${offerInfo.regularPrice}. Empty = no sale.`}
              style={{ flex: "1 1 160px" }}
            />
            <TextField
              label="Starts"
              type="datetime-local"
              value={toLocalInput(configForm.offerSaleStartsAt)}
              onChange={(e) => setConfigForm({ ...configForm, offerSaleStartsAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
              InputLabelProps={{ shrink: true }}
              helperText="Empty = straight away"
              style={{ flex: "1 1 200px" }}
            />
            <TextField
              label="Ends"
              type="datetime-local"
              value={toLocalInput(configForm.offerSaleEndsAt)}
              onChange={(e) => setConfigForm({ ...configForm, offerSaleEndsAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
              InputLabelProps={{ shrink: true }}
              helperText="Required for a sale"
              style={{ flex: "1 1 200px" }}
            />
          </div>
          <Button
            size="small"
            onClick={() => setConfigForm({ ...configForm, offerSalePrice: "", offerSaleStartsAt: null, offerSaleEndsAt: null })}
            style={{ marginBottom: "10px" }}
          >
            Clear sale
          </Button>

          <h3 style={{ marginTop: "20px" }}>Time-limited offer (first call/chat tap)</h3>
          <p style={{ marginTop: "0", marginBottom: "10px", color: "#666", fontSize: "14px" }}>
            Once per member: the first time a call or chat tap needs a payment, the app shows this package at the
            lower price with a countdown, instead of the package list. When the time is over (or after they buy),
            everything is as before. Changes apply to members who haven't seen it yet.
          </p>
          {flashStats && (
            <p style={{ margin: "0 0 10px", fontWeight: 600, color: "#444" }}>
              Shown to {flashStats.shown} members · running now {flashStats.running} · bought {flashStats.bought}
              {flashStats.shown ? ` (${((100 * flashStats.bought) / flashStats.shown).toFixed(1)}%)` : ""}
            </p>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={configForm.flashOfferEnabled !== false}
                onChange={(e) => setConfigForm({ ...configForm, flashOfferEnabled: e.target.checked })}
                color="primary"
              />
            }
            label="Show the time-limited offer"
          />
          <div style={{ display: "flex", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
            <TextField
              select
              label="Package"
              value={configForm.flashOfferPackageId || "2000"}
              onChange={(e) => setConfigForm({ ...configForm, flashOfferPackageId: e.target.value })}
              disabled={configForm.flashOfferEnabled === false}
              style={{ flex: "1 1 220px" }}
              helperText="Sold for 1 month at the offer price"
            >
              {packages.filter((p) => p.isActive).map((p) => (
                <MenuItem key={p.packageId} value={String(p.packageId)}>
                  ৳{p.price} package · {p.contacts} contacts
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Offer price (BDT)"
              type="number"
              value={configForm.flashOfferPrice ?? 1000}
              onChange={(e) => setConfigForm({ ...configForm, flashOfferPrice: e.target.value })}
              disabled={configForm.flashOfferEnabled === false}
              style={{ flex: "1 1 140px" }}
              helperText="Less than the package price"
            />
            <TextField
              label="Minutes"
              type="number"
              value={configForm.flashOfferMinutes ?? 60}
              onChange={(e) => setConfigForm({ ...configForm, flashOfferMinutes: e.target.value })}
              disabled={configForm.flashOfferEnabled === false}
              inputProps={{ min: 5, max: 1440 }}
              style={{ flex: "1 1 110px" }}
              helperText="Countdown length"
            />
          </div>

          <h3 style={{ marginTop: "20px" }}>Interests and shortlist (free members)</h3>
          <p style={{ marginTop: "0", marginBottom: "10px", color: "#666", fontSize: "14px" }}>
            Members with a running package are never limited. A second tap on someone they already sent to, and
            someone whose contact they paid for, don't count. Over the limit the app and website say today's free
            interests are used up and offer the packages.
          </p>
          {interestStats && (
            <p style={{ margin: "0 0 10px", fontWeight: 600, color: "#444" }}>
              Today: {interestStats.usedToday} members sent free interests · {interestStats.atLimitToday} used all of them
            </p>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={configForm.freeInterestLimitEnabled === true}
                onChange={(e) => setConfigForm({ ...configForm, freeInterestLimitEnabled: e.target.checked })}
                color="primary"
              />
            }
            label="Limit free members' interests"
          />
          <div style={{ display: "flex", gap: "10px", marginBottom: "6px", flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              label="Free interests a day"
              type="number"
              value={configForm.freeInterestDailyLimit ?? 3}
              onChange={(e) => setConfigForm({ ...configForm, freeInterestDailyLimit: e.target.value })}
              disabled={configForm.freeInterestLimitEnabled !== true}
              inputProps={{ min: 0, max: 100 }}
              style={{ flex: "0 1 180px" }}
              helperText="New day at midnight (Bangladesh)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={configForm.freeInterestLimitWomen === true}
                  onChange={(e) => setConfigForm({ ...configForm, freeInterestLimitWomen: e.target.checked })}
                  disabled={configForm.freeInterestLimitEnabled !== true}
                  color="primary"
                />
              }
              label="Limit women too (off = women unlimited)"
            />
          </div>
          <FormControlLabel
            control={
              <Switch
                checked={configForm.shortlistAlertNeedsPackage === true}
                onChange={(e) => setConfigForm({ ...configForm, shortlistAlertNeedsPackage: e.target.checked })}
                color="primary"
              />
            }
            label={'"Someone shortlisted you" to women only from men with a package (shortlisting itself stays free)'}
          />
          <FormControlLabel
            control={
              <Switch
                checked={configForm.shortlistViewNeedsPackage === true}
                onChange={(e) => setConfigForm({ ...configForm, shortlistViewNeedsPackage: e.target.checked })}
                color="primary"
              />
            }
            label="Only members with a package can see who shortlisted them (others see a locked “someone shortlisted you”)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={configForm.shortlistViewLockWomen === true}
                onChange={(e) => setConfigForm({ ...configForm, shortlistViewLockWomen: e.target.checked })}
                disabled={configForm.shortlistViewNeedsPackage !== true}
                color="primary"
              />
            }
            label="Lock it for women too (off = women always see who)"
          />

          <h3 style={{ marginTop: "20px" }}>Dashboard offer banner</h3>
          <p style={{ marginTop: "0", marginBottom: "10px", color: "#666", fontSize: "14px" }}>
            One banner at the top of the dashboard (app and website) with the one offer that matters most for that
            member, in the order below. Face-verified members only. Members with a running package see it only when
            the package is ending. Members can close it for the day.
          </p>
          <FormControlLabel
            control={
              <Switch
                checked={configForm.homeOfferEnabled === true}
                onChange={(e) => setConfigForm({ ...configForm, homeOfferEnabled: e.target.checked })}
                color="primary"
              />
            }
            label="Show the offer banner on the dashboard"
          />
          <div style={{ display: "flex", flexDirection: "column", marginLeft: "12px", marginBottom: "6px" }}>
            {HOME_OFFERS.map(([key, label], index) => {
              const kinds = Array.isArray(configForm.homeOfferKinds) ? configForm.homeOfferKinds : ALL_HOME_OFFERS;
              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={kinds.includes(key)}
                      disabled={configForm.homeOfferEnabled !== true}
                      onChange={(e) =>
                        setConfigForm({
                          ...configForm,
                          homeOfferKinds: e.target.checked ? [...new Set([...kinds, key])] : kinds.filter((k) => k !== key),
                        })
                      }
                    />
                  }
                  label={`${index + 1}. ${label}`}
                />
              );
            })}
          </div>

          <h3 style={{ marginTop: "20px" }}>Duration Discounts</h3>
          {configForm.durations && configForm.durations.map((duration, index) => (
            <div key={index} style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
              <TextField
                label="Label"
                value={duration.label}
                onChange={(e) => handleDurationChange(index, "label", e.target.value)}
                style={{ flex: 2 }}
              />
              <TextField
                label="Discount (%)"
                type="number"
                value={duration.discount}
                onChange={(e) => handleDurationChange(index, "discount", parseInt(e.target.value) || 0)}
                style={{ flex: 1 }}
              />
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfigDialog}>Cancel</Button>
          <Button onClick={handleSaveConfig} variant="contained" color="primary">
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Packages;
